import { useCallback, useEffect, useMemo, useState } from 'react';
import { PackagePlus, X } from 'lucide-react';
import type { CandidatoVentaVista, CategoriaVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { pesosACentavos } from '@shared/centavos';
import { conConfirmacionDeNombre } from '@renderer/lib/confirmarNombreParecido';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 72 — "Vender del inventario": el catálogo se captura UNA vez.
 *
 * La dueña cargó 27 ingredientes y el sistema le pedía capturar lo mismo otra vez, uno por uno,
 * como productos. La pregunta que llegó fue *"¿es justo?"* — y no lo era: la mayoría de esos 27 se
 * venden **tal cual**, y ahí el ingrediente y el producto son el mismo objeto.
 *
 * El costo real de la doble captura no era el tiempo, eran los errores: en esa segunda pasada
 * aparecieron cuatro (dos productos de lomo descontando de la costilla, un vaso consumiendo 20
 * litros, una salsa consumiendo 0.001 pieza), porque hay que elegir de un desplegable de 27 nombres
 * alfabéticos y volver a escribir la medida. Aquí el ingrediente ya está elegido y la medida viene
 * propuesta: no hay dónde equivocarse.
 *
 * Lo único que pide es el **precio**, que es el único dato que el sistema no puede saber.
 */

interface Props {
  categorias: CategoriaVista[];
  onCerrar: () => void;
  onCreados: (cuantos: number) => void;
}

interface Fila {
  insumoId: number;
  marcado: boolean;
  nombre: string;
  /** En pesos, como lo teclea la persona. Se convierte a centavos al enviar. */
  precio: number;
  consumo: number;
}

export function VenderDelInventario({ categorias, onCerrar, onCreados }: Props) {
  const [candidatos, setCandidatos] = useState<CandidatoVentaVista[] | null>(null);
  const [filas, setFilas] = useState<Record<number, Fila>>({});
  const [categoriaId, setCategoriaId] = useState<number | ''>('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    const res = await window.api.productos.candidatosVenta();
    if (!res.ok) {
      setError(traducirErrorBackend(res.error));
      setCandidatos([]);
      return;
    }
    setCandidatos(res.data);
    setFilas(
      Object.fromEntries(
        res.data.map((c) => [
          c.insumoId,
          {
            insumoId: c.insumoId,
            // Premarcados sólo los que se venden claramente tal cual. Marcar los 27 de default
            // haría que la dueña tuviera que desmarcar las carnes, que es más trabajo y más
            // riesgo que marcar las siete que sí.
            marcado: c.ventaDirectaProbable,
            nombre: c.nombre,
            precio: 0,
            consumo: c.consumoSugerido,
          },
        ]),
      ),
    );
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cambiar = (insumoId: number, cambio: Partial<Fila>) =>
    setFilas((prev) => ({ ...prev, [insumoId]: { ...prev[insumoId], ...cambio } }));

  const seleccionadas = useMemo(
    () => Object.values(filas).filter((f) => f.marcado),
    [filas],
  );
  const sinPrecio = seleccionadas.filter((f) => f.precio <= 0).length;
  const sinConsumo = seleccionadas.filter((f) => f.consumo <= 0).length;

  const guardar = async () => {
    setError(null);
    if (seleccionadas.length === 0) {
      setError('Marca al menos un ingrediente.');
      return;
    }
    if (sinPrecio > 0) {
      setError(
        `Falta el precio de ${sinPrecio} ${sinPrecio === 1 ? 'producto' : 'productos'}. Es lo único que el sistema no puede saber.`,
      );
      return;
    }
    if (sinConsumo > 0) {
      setError(
        `Falta indicar cuánto sale en cada venta de ${sinConsumo} ${sinConsumo === 1 ? 'ingrediente' : 'ingredientes'}. Si se entrega completo, es 1.`,
      );
      return;
    }

    setEnviando(true);
    const res = await conConfirmacionDeNombre((confirmado) => window.api.productos.crearDesdeInventario({
      nombreParecidoConfirmado: confirmado || undefined,
      categoriaId: categoriaId === '' ? undefined : categoriaId,
      items: seleccionadas.map((f) => ({
        insumoId: f.insumoId,
        nombre: f.nombre.trim(),
        precio: pesosACentavos(f.precio),
        consumo: f.consumo,
      })),
    }));
    setEnviando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    onCreados(res.data.length);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold">
              <PackagePlus className="h-5 w-5" />
              Vender del inventario
            </h2>
            <p className="mt-0.5 text-sm text-slate-600">
              Los ingredientes que ya cargaste y todavía no están en el menú. Marca los que se
              venden, ponles precio, y se crean solos — sin capturarlos otra vez.
            </p>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-1" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-3">
          {candidatos === null ? (
            <p className="py-6 text-center text-sm text-slate-500">Buscando ingredientes…</p>
          ) : candidatos.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              Todos tus ingredientes ya están en el menú. Nada que hacer aquí.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-8 pb-2"></th>
                  <th className="pb-2">Se vende como</th>
                  <th className="w-28 pb-2 text-right">Precio</th>
                  <th className="w-28 pb-2 text-right">Sale por venta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {candidatos.map((c) => {
                  const f = filas[c.insumoId];
                  if (!f) return null;
                  return (
                    <tr key={c.insumoId} className={f.marcado ? '' : 'opacity-60'}>
                      <td className="py-2 align-top">
                        <input
                          type="checkbox"
                          className="mt-2 h-5 w-5"
                          checked={f.marcado}
                          onChange={(e) => cambiar(c.insumoId, { marcado: e.target.checked })}
                          aria-label={`Vender ${c.nombre}`}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        {/*
                          El nombre es editable: el ingrediente se llama "Coca Cola Clasica" y en el
                          botón del punto de venta quizá quiera "Coca Cola". Se propone el del
                          inventario porque nueve de cada diez veces es el mismo.
                        */}
                        <input
                          className="input w-full"
                          value={f.nombre}
                          onChange={(e) => cambiar(c.insumoId, { nombre: e.target.value })}
                          disabled={!f.marcado}
                        />
                        <p className="mt-0.5 text-xs text-slate-500">
                          {c.razon} Hay {c.stockActual} {c.unidad}.
                        </p>
                      </td>
                      <td className="py-2 align-top">
                        <NumberField
                          className="input w-full text-right"
                          min={0}
                          decimals={2}
                          value={f.precio}
                          onChange={(n) => cambiar(c.insumoId, { precio: n })}
                          disabled={!f.marcado}
                          ariaLabel={`Precio de ${c.nombre}`}
                        />
                      </td>
                      <td className="py-2 align-top">
                        <div className="flex items-center gap-1">
                          <NumberField
                            className="input w-full text-right"
                            min={0}
                            decimals={3}
                            value={f.consumo}
                            onChange={(n) => cambiar(c.insumoId, { consumo: n })}
                            disabled={!f.marcado}
                            ariaLabel={`Cuánto ${c.unidad} sale por venta de ${c.nombre}`}
                          />
                          <span className="shrink-0 text-xs text-slate-500">{c.unidad}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm">
              Categoría para todos:{' '}
              <select
                className="input inline-block w-auto"
                value={categoriaId}
                onChange={(e) => setCategoriaId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">Sin categoría</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </label>
            <span className="text-xs text-slate-500">
              Si son de varias categorías, hazlo en dos tandas: es más rápido que corregir después.
            </span>
          </div>

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600">
              {seleccionadas.length === 0
                ? 'Ninguno marcado'
                : `${seleccionadas.length} ${seleccionadas.length === 1 ? 'producto' : 'productos'} por crear`}
            </span>
            <div className="flex gap-2">
              <button onClick={onCerrar} className="btn-ghost min-h-[44px]" disabled={enviando}>
                Cancelar
              </button>
              <button
                onClick={() => void guardar()}
                className="btn-primary min-h-[44px]"
                disabled={enviando || seleccionadas.length === 0}
              >
                {enviando ? 'Creando…' : `Crear ${seleccionadas.length || ''}`.trim()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
